import { Injectable } from '@jack-henry/frontend-utils/di';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import { AlarmClock } from './alarm-clock';
import { Time } from './time';

export interface AlarmConfig {
    key: string;
    time: string | Time;
}

export interface ClockConfig {
    fiTime: string;
    timeZone: string;
    alarms: AlarmConfig[];
}

@Injectable()
export class AlarmClockService {
    private readonly clock = AlarmClock.getInstance();

    private readonly alarmSources = new Map<string, ObservationSource<void>>();

    public init(config: ClockConfig): void {
        this.clock.time = config.fiTime;
        this.clock.timeZone = config.timeZone;
        config.alarms.forEach(({ key, time }) => this.clock.setAlarm(key, time));
    }

    public alarm$(key: string) {
        if (!this.alarmSources.has(key)) {
            const source = new ObservationSource<void>();
            this.alarmSources.set(key, source);
            this.clock.addEventListener(key, () => source.emit(undefined));
        }

        const source = this.alarmSources.get(key);
        if (!source) {
            throw new Error(`Alarm source for "${key}" could not be created`);
        }

        return source.toObservable();
    }

    public get timeZone() {
        return this.clock.timeZone;
    }

    public checkReadiness() {
        return this.clock.checkReadiness();
    }

    public getAlarm(key: string) {
        return this.clock.getAlarm(key);
    }

    public isBefore(key: string) {
        return this.clock.isBefore(key);
    }

    public isAfter(key: string) {
        return this.clock.isAfter(key);
    }

    public isBetween(start: string, end: string) {
        return this.clock.isBetween(start, end);
    }
}
